/**
 * VAP Quick View
 * Opens a modal with image slider, variant picker, and add-to-cart.
 * Only triggered for products with multiple variants.
 */
(function () {
  'use strict';

  class VapQuickView {
    constructor() {
      this.activeModal = null;
      this.productId = null;
      this.currentSlide = 0;
      this.slides = [];
      this.dots = [];
      this.track = null;
      this.variantsData = [];
      this.selectedOptions = [];
      this.numOptions = 0;
      this._moneyFormat = '${{amount}}';

      document.addEventListener('click', this._onClick.bind(this));
      document.addEventListener('keydown', this._onKeyDown.bind(this));
      this._teleportModals();
      this._watchCartDrawer();
    }

    /* Move all overlays to <body> so position:fixed is relative to viewport,
       not a transformed ancestor (.vcol-card-wrap has transform set) */
    _teleportModals() {
      document.querySelectorAll('.vap-qv-overlay').forEach(function (el) {
        document.body.appendChild(el);
      });
    }

    /* Watch cart drawer — close modal when drawer opens */
    _watchCartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      new MutationObserver(() => {
        if (
          this.activeModal &&
          (drawer.classList.contains('is-open') || drawer.hasAttribute('open'))
        ) {
          this.close();
        }
      }).observe(drawer, { attributes: true, attributeFilter: ['class', 'open'] });
    }

    _onClick(e) {
      /* Open trigger */
      const trigger = e.target.closest('[data-vap-qv-open]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        this.open(trigger.dataset.vapQvOpen);
        return;
      }

      if (!this.activeModal) return;

      /* Backdrop click */
      if (e.target === this.activeModal) { this.close(); return; }

      /* Close button */
      if (e.target.closest('.vap-qv-close')) { this.close(); return; }

      /* Slider arrows */
      if (e.target.closest('.vap-qv-arrow--prev')) { this._prevSlide(); return; }
      if (e.target.closest('.vap-qv-arrow--next')) { this._nextSlide(); return; }

      /* Dots */
      const dot = e.target.closest('.vap-qv-dot');
      if (dot) { this._goTo(parseInt(dot.dataset.index, 10)); return; }

      /* Variant option buttons */
      const optBtn = e.target.closest('.vap-qv-opt-btn');
      if (optBtn && !optBtn.classList.contains('is-unavailable')) {
        this._selectOption(optBtn);
      }
    }

    _onKeyDown(e) {
      if (!this.activeModal) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowLeft') this._prevSlide();
      if (e.key === 'ArrowRight') this._nextSlide();
    }

    open(productId) {
      let modal = document.getElementById('vap-qv-' + productId);

      if (!modal) {
        /* Hydrate from <template> on first open — avoids upfront DOM bloat */
        const tpl = document.getElementById('vap-qv-tpl-' + productId);
        if (!tpl) return;
        document.body.appendChild(tpl.content.cloneNode(true));
        modal = document.getElementById('vap-qv-' + productId);
        if (!modal) return;
      }

      this.activeModal = modal;
      this.productId = productId;
      this.currentSlide = 0;

      /* Slider elements */
      this.track = modal.querySelector('.vap-qv-track');
      this.slides = Array.from(modal.querySelectorAll('.vap-qv-slide'));
      this.dots = Array.from(modal.querySelectorAll('.vap-qv-dot'));

      /* Load lazy images now that modal is opening */
      modal.querySelectorAll('img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });

      /* Reset to first slide instantly */
      this._goTo(0, true);

      /* Parse variant data */
      const dataEl = document.getElementById('vap-qv-data-' + productId);
      if (dataEl) {
        try {
          const data = JSON.parse(dataEl.textContent);
          this.variantsData = data.variants || [];
          this._moneyFormat = data.moneyFormat || '${{amount}}';
        } catch (_) {}
      }

      /* Read current selected options from UI */
      this.selectedOptions = [];
      this.numOptions = 0;
      modal.querySelectorAll('.vap-qv-option-group').forEach((group, i) => {
        this.numOptions++;
        const sel = group.querySelector('.vap-qv-opt-btn.is-selected');
        this.selectedOptions[i] = sel ? sel.dataset.value : '';
      });

      /* Show modal */
      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('is-active');
      requestAnimationFrame(() => modal.classList.add('is-open'));
      document.body.classList.add('vap-qv-open');
      modal.querySelector('.vap-qv-close')?.focus();
    }

    close() {
      if (!this.activeModal) return;
      const modal = this.activeModal;
      this.activeModal = null;
      this.productId = null;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('vap-qv-open');
      setTimeout(() => modal.classList.remove('is-active'), 280);
    }

    /* ---- Slider ---- */
    _goTo(index, instant) {
      this.currentSlide = Math.max(0, Math.min(index, this.slides.length - 1));
      if (this.track) {
        if (instant) {
          this.track.style.transition = 'none';
          void this.track.offsetWidth; /* flush transition removal */
        } else {
          this.track.style.transition = '';
        }
        this.track.style.transform = `translateX(-${this.currentSlide * 100}%)`;
      }
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === this.currentSlide));
    }

    _prevSlide() {
      this._goTo(this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1);
    }

    _nextSlide() {
      this._goTo(this.currentSlide === this.slides.length - 1 ? 0 : this.currentSlide + 1);
    }

    /* ---- Variant selection ---- */
    _selectOption(btn) {
      const optionIndex = parseInt(btn.dataset.optionIndex, 10);
      const value = btn.dataset.value;

      /* Update button states */
      btn.closest('.vap-qv-option-group')
        .querySelectorAll('.vap-qv-opt-btn')
        .forEach((b) => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      /* Update label */
      const label = btn.closest('.vap-qv-option-group').querySelector('.vap-qv-option-selected');
      if (label) label.textContent = value;

      this.selectedOptions[optionIndex] = value;

      const variant = this._findVariant();
      if (variant) this._applyVariant(variant);
    }

    _findVariant() {
      return this.variantsData.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return this.selectedOptions.every((sel, i) => i >= this.numOptions || opts[i] === sel);
      });
    }

    _applyVariant(variant) {
      /* Update hidden variant input */
      const input = document.getElementById('vap-qv-vid-' + this.productId);
      if (input) input.value = variant.id;

      /* Update price display */
      const priceWrap = document.getElementById('vap-qv-price-' + this.productId);
      if (priceWrap) {
        let html = '';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const pct = Math.round(
            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
          );
          html += `<span class="vap-qv-price--compare">${this._money(variant.compare_at_price)}</span>`;
          html += `<span class="vap-qv-price--current">${this._money(variant.price)}</span>`;
          html += `<span class="vap-qv-badge-sale">-${pct}%</span>`;
        } else {
          html += `<span class="vap-qv-price--current">${this._money(variant.price)}</span>`;
        }
        priceWrap.innerHTML = html;
      }

      /* Update ATC button */
      const btn = document.getElementById('vap-qv-btn-' + this.productId);
      if (btn) {
        btn.disabled = !variant.available;
        const span = btn.querySelector('span');
        if (span) span.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }

      /* Jump to variant image if different */
      if (variant.featured_image && variant.featured_image.position) {
        const imgIndex = variant.featured_image.position - 1;
        if (imgIndex !== this.currentSlide) this._goTo(imgIndex);
      }
    }

    _money(cents) {
      const amount = (cents / 100).toFixed(2);
      const amountInt = Math.round(cents / 100).toString();
      return (this._moneyFormat || '${{amount}}')
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', amountInt)
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
        .replace(
          '{{amount_no_decimals_with_comma_separator}}',
          amountInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.vapQuickView = new VapQuickView();
    });
  } else {
    window.vapQuickView = new VapQuickView();
  }
})();
